import * as THREE from 'three'
import { useAppStore } from '@core/store'
import type { VpModelRef } from '@viewer/lib/viewpoint'
import { getModelsBbox } from '@viewer/section-box/sectionMath'
import type { CaixaSimples } from '@viewer/bcf/lib/pinAncora'
import {
  escolherReferencial,
  modelosGeoDaCena,
  type EscolhaDeReferencial,
  type ModeloGeo,
} from '@viewer/clash/clashReferencial'
import type { Referencial } from './bcfCoordenadas'
import {
  escolherLeitura,
  type CandidatoDeReferencial,
  type LeituraEscolhida,
} from './bcfVistaImportada'

/**
 * O referencial das coordenadas de um `.bcfv`, na escrita e na leitura.
 *
 * Regra comum (a mesma da exportação dos conflitos, `escolherReferencial`
 * em `viewer/clash/clashReferencial.ts`), aplicada aos modelos do
 * viewpoint:
 *
 *   1. os modelos abertos que estejam nas `model_refs` do viewpoint (por
 *      sha256); sem `model_refs`, ou sem nenhum aberto, todos os modelos
 *      abertos;
 *   2. desses, o primeiro sem IfcMapConversion (o ficheiro e o mundo da
 *      federação coincidem: no Cha139, o ARQ, o MAC, o HID e o EEL têm o
 *      PT-TM06 embutido);
 *   3. se todos tiverem MapConversion, o mundo georreferenciado (o CRS);
 *   4. sem modelos, a identidade.
 *
 * Não depende do modelo activo. Antes, a importação lia com o modelo
 * activo: com o EST (MapConversion) activo, a câmara de um BCF exportado
 * pelos conflitos ficava a cerca de 150 km do edifício.
 *
 * Um `.bcfv` de outra plataforma não traz `model_refs` (é uma extensão
 * nossa) e pode estar nas coordenadas locais de um modelo com
 * MapConversion. Por isso a LEITURA não fica só pela regra: tenta-a
 * primeiro e, se a câmara não cair junto dos modelos abertos, tenta o
 * referencial de cada modelo e o mundo (`candidatosDeLeitura`,
 * `lerVisualizacaoDaCena`). A ESCRITA usa só a regra.
 */
export function escolherReferencialDoViewpoint(
  modelos: readonly ModeloGeo[],
  modelRefs: readonly VpModelRef[] = [],
): EscolhaDeReferencial {
  const shas = new Set(modelRefs.map((r) => r.sha256))
  const doViewpoint = modelos.filter((m) => m.sha256 && shas.has(m.sha256))
  return escolherReferencial(doViewpoint.length > 0 ? doViewpoint : modelos)
}

function mesmaMatriz(a: Referencial | null, b: Referencial | null): boolean {
  const ma = new THREE.Matrix4()
  const mb = new THREE.Matrix4()
  if (a && a.length === 16) ma.fromArray(a as number[])
  if (b && b.length === 16) mb.fromArray(b as number[])
  // Milímetro nas translações (coordenadas de mapa na casa das centenas
  // de milhares) e 1e-6 na parte linear.
  return ma.elements.every((v, i) => Math.abs(v - mb.elements[i]) <= (i >= 12 ? 1e-3 : 1e-6))
}

/**
 * Os referenciais a tentar na leitura de um `.bcfv`, por ordem: o da
 * regra; o de cada modelo do viewpoint (pelas `model_refs`); o do modelo
 * activo; o dos outros modelos; o mundo. Sem repetidos (o de um modelo
 * sem MapConversion é o mundo).
 */
export function candidatosDeLeitura(
  modelos: readonly ModeloGeo[],
  modelRefs: readonly VpModelRef[] = [],
  activoId: string | null = null,
): CandidatoDeReferencial[] {
  const regra = escolherReferencialDoViewpoint(modelos, modelRefs)
  const out: CandidatoDeReferencial[] = [{ referencial: regra.referencial, origem: `regra: ${regra.rotulo}` }]
  const shas = new Set(modelRefs.map((r) => r.sha256))
  const ordem = [
    ...modelos.filter((m) => m.sha256 && shas.has(m.sha256)),
    ...modelos.filter((m) => m.id === activoId),
    ...modelos,
  ]
  const juntar = (referencial: Referencial | null, origem: string) => {
    if (out.some((c) => mesmaMatriz(c.referencial, referencial))) return
    out.push({ referencial, origem })
  }
  for (const m of ordem) if (m.referencial) juntar(m.referencial, `modelo ${m.fileName}`)
  juntar(null, 'mundo')
  return out
}

/**
 * O referencial dos modelos abertos para um viewpoint, pela regra comum.
 * É o que a exportação do módulo BCF usa para escrever a câmara e os
 * cortes de um viewpoint nosso; sem modelos, null (identidade).
 */
export function referencialDaCena(
  modelRefs: readonly VpModelRef[] = [],
): Referencial | null {
  return escolherReferencialDoViewpoint(modelosGeoDaCena(), modelRefs).referencial
}

/** O que a leitura precisa da cena, lido uma vez (a importação lê dezenas
 *  de viewpoints seguidos). */
export interface ContextoDeLeitura {
  modelos: ModeloGeo[]
  activoId: string | null
  /** Caixa dos modelos visíveis, no mundo three. */
  caixa: CaixaSimples | null
}

export function contextoDaCena(): ContextoDeLeitura {
  const caixa = getModelsBbox()
  return {
    modelos: modelosGeoDaCena(),
    activoId: useAppStore.getState().activeModelId ?? null,
    caixa: caixa
      ? {
          min: { x: caixa.min.x, y: caixa.min.y, z: caixa.min.z },
          max: { x: caixa.max.x, y: caixa.max.y, z: caixa.max.z },
        }
      : null,
  }
}

/**
 * Lê o `.bcfv` original de um viewpoint importado na cena de agora: a
 * regra primeiro, os outros referenciais se a câmara não for plausível
 * (`escolherLeitura`). Null quando o XML não se lê. Nunca lança.
 */
export function lerVisualizacaoDaCena(
  xml: string,
  modelRefs: readonly VpModelRef[] = [],
  contexto?: ContextoDeLeitura,
): LeituraEscolhida | null {
  let ctx = contexto
  if (!ctx) {
    try {
      ctx = contextoDaCena()
    } catch {
      ctx = { modelos: [], activoId: null, caixa: null }
    }
  }
  return escolherLeitura(xml, candidatosDeLeitura(ctx.modelos, modelRefs, ctx.activoId), ctx.caixa)
}
